const RESIZE_DEBOUNCE_MS = 150,
	 MINIMUM_CARD_COUNT = 2,
	 MINIMUM_POSITION_COUNT = 2,
	 POSITION_TOLERANCE_PX = 1,
	 ACTIVE_MARKER_VALUE = '1',
	 FALLBACK_DOT_NUMBER_OFFSET = 1,
	 DEFAULT_ACTIVE_DOT_CLASS = 'is-active';

export default class ScrollCarouselDots {
	constructor(options) {
		this.markerProperty = options.markerProperty;
		this.cardSelector = options.cardSelector;
		this.dotsClassName = options.dotsClassName;
		this.dotClassName = options.dotClassName;
		this.activeDotClassName = options.activeDotClassName || DEFAULT_ACTIVE_DOT_CLASS;
		this.dotsContainer = null;
		this.scrollPositions = [];
		this.handleScroll = null;

		this.scrollContainer = document.querySelector(options.scrollContainerSelector);

		if (!this.scrollContainer) {
			return;
		}

		this.cards = [...this.scrollContainer.querySelectorAll(this.cardSelector)];

		if (this.cards.length < MINIMUM_CARD_COUNT) {
			return;
		}

		this.syncEnabledState();
		window.addEventListener('resize', this.debounce(this.syncEnabledState.bind(this), RESIZE_DEBOUNCE_MS));
	}

	isCarouselActive() {
		const markerValue = getComputedStyle(this.scrollContainer)
			.getPropertyValue(this.markerProperty)
			.trim();

		return markerValue === ACTIVE_MARKER_VALUE;
	}

	syncEnabledState() {
		if (this.isCarouselActive() && this.isScrollable()) {
			this.enable();
		} else {
			this.disable();
		}
	}

	isScrollable() {
		return this.scrollContainer.scrollWidth > this.scrollContainer.clientWidth;
	}

	enable() {
		this.scrollPositions = this.computeScrollPositions();

		if (this.scrollPositions.length < MINIMUM_POSITION_COUNT) {
			this.disable();

			return;
		}

		this.rebuildDots();

		if (!this.handleScroll) {
			this.handleScroll = this.throttleWithRaf(() => this.updateActiveDot());
			this.scrollContainer.addEventListener('scroll', this.handleScroll, {'passive': true});
		}

		this.updateActiveDot();
	}

	disable() {
		if (this.handleScroll) {
			this.scrollContainer.removeEventListener('scroll', this.handleScroll);
			this.handleScroll = null;
		}

		if (this.dotsContainer) {
			this.dotsContainer.remove();
			this.dotsContainer = null;
		}
	}

	computeScrollPositions() {
		const maxScrollLeft = this.scrollContainer.scrollWidth - this.scrollContainer.clientWidth,
			  scrollPositions = [];

		this.cards.forEach((card, cardIndex) => {
			const offset = Math.min(this.getCardScrollOffset(card), maxScrollLeft),
				  lastPosition = scrollPositions.at(-1);

			if (!lastPosition || Math.abs(offset - lastPosition.offset) > POSITION_TOLERANCE_PX) {
				scrollPositions.push({offset, cardIndex});
			}
		});

		return scrollPositions;
	}

	rebuildDots() {
		if (this.dotsContainer) {
			this.dotsContainer.remove();
			this.dotsContainer = null;
		}

		this.buildDots();
	}

	buildDots() {
		const dotsContainer = document.createElement('div');

		dotsContainer.className = this.dotsClassName;

		this.scrollPositions.forEach((scrollPosition, positionIndex) => {
			const card = this.cards[scrollPosition.cardIndex],
				  cardTitle = card && card.querySelector('.name'),
				  dot = document.createElement('button');

			dot.type = 'button';
			dot.className = this.dotClassName;
			dot.setAttribute(
				'aria-label',
				cardTitle ? cardTitle.textContent.trim() : String(positionIndex + FALLBACK_DOT_NUMBER_OFFSET)
			);
			dot.addEventListener('click', () => this.scrollToPosition(positionIndex));
			dotsContainer.append(dot);
		});

		this.dotsContainer = dotsContainer;
		this.scrollContainer.insertAdjacentElement('afterend', dotsContainer);
	}

	scrollToPosition(positionIndex) {
		const scrollPosition = this.scrollPositions[positionIndex];

		if (!scrollPosition) {
			return;
		}

		this.scrollContainer.scrollTo({
			'left'    : scrollPosition.offset,
			'behavior': 'smooth'
		});
	}

	updateActiveDot() {
		this.setActiveDot(this.getCurrentPositionIndex());
	}

	getCurrentPositionIndex() {
		const currentScrollLeft = this.scrollContainer.scrollLeft;

		let closestPositionIndex = 0,
			smallestDistance = Number.POSITIVE_INFINITY;

		this.scrollPositions.forEach((scrollPosition, positionIndex) => {
			const distance = Math.abs(scrollPosition.offset - currentScrollLeft);

			if (distance < smallestDistance) {
				smallestDistance = distance;
				closestPositionIndex = positionIndex;
			}
		});

		return closestPositionIndex;
	}

	getCardScrollOffset(card) {
		const cardLeft = card.getBoundingClientRect().left,
			  containerLeft = this.scrollContainer.getBoundingClientRect().left;

		return cardLeft - containerLeft + this.scrollContainer.scrollLeft;
	}

	setActiveDot(activeIndex) {
		if (!this.dotsContainer) {
			return;
		}

		[...this.dotsContainer.children].forEach((dot, dotIndex) => {
			dot.classList.toggle(this.activeDotClassName, dotIndex === activeIndex);
		});
	}

	throttleWithRaf(callback) {
		let isScheduled = false;

		return () => {
			if (isScheduled) {
				return;
			}

			isScheduled = true;

			requestAnimationFrame(() => {
				isScheduled = false;
				callback();
			});
		};
	}

	debounce(callback, waitMs) {
		let timeoutId;

		return (...args) => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => callback(...args), waitMs);
		};
	}
}